/**
 * The one confirmation contract (TMM-B4, TMMRSL-0067): `title` names the object, `consequence`
 * states what happens (ACT-007), `confirmLabel` names the action, and the dismiss is "Cancel" —
 * or a "Keep …" `cancelLabel` when the action itself is a cancel. `destructive` makes the confirm
 * error-coloured; otherwise it is primary (ACT-003).
 * `onConfirm` runs the action; the dialog stays open while it runs (the confirm is a SubmitButton
 * that shows its progress) and shows `errorMessage(err)` inline when it rejects - with the
 * "Reference: {id}" caption when `err` is an `ApiError` carrying a request id (TMMRSL-0071) - so the
 * caller only closes it on success. A close that does not go through `close()` (the parent sets
 * `open` false) keeps the alert through the exit; the next rising edge of `open` clears it, so a
 * later open does not paint the previous failure (TMMRSL-0082). Initial focus is on the dismiss,
 * so Enter never fires the action by
 * accident (DLG-004); MUI returns focus to the opener on close. The dialog keeps the last title,
 * consequence and labels it was open with while it fades out, so a caller that clears its target on
 * close never shows "Suspend ?" mid-exit (TMMRSL-0068).
 */
import { useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { ActionErrorAlert } from './ActionErrorAlert';
import { SubmitButton } from './SubmitButton';

export interface ConfirmDialogProps {
  open: boolean;
  /** Names the object (ACT-007). */
  title: string;
  /** States what happens. */
  consequence: string;
  /** Names the action. */
  confirmLabel: string;
  /** Defaults to "Cancel"; "Keep …" when the action itself is a cancel. */
  cancelLabel?: string;
  /** Error-coloured confirm for a destructive action; primary otherwise. */
  destructive?: boolean;
  /** An external busy flag; the dialog also tracks its own while `onConfirm` runs. */
  busy?: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  errorMessage: (err: unknown) => string;
  /** An action beside the error (a link to where it can be fixed); `null` for none. */
  errorAction?: (err: unknown) => ReactNode;
}

export function ConfirmDialog({
  open,
  title: openTitle,
  consequence: openConsequence,
  confirmLabel: openConfirmLabel,
  cancelLabel: openCancelLabel,
  destructive: openDestructive = false,
  busy: externalBusy = false,
  onConfirm,
  onCancel,
  errorMessage,
  errorAction,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  // The text props as of the last render with `open` true; rendered during the exit transition.
  const current = {
    title: openTitle,
    consequence: openConsequence,
    confirmLabel: openConfirmLabel,
    cancelLabel: openCancelLabel,
    destructive: openDestructive,
  };
  const [last, setLast] = useState(current);
  if (
    open &&
    (Object.keys(current) as Array<keyof typeof current>).some((k) => last[k] !== current[k])
  ) {
    setLast(current);
  }
  const { title, consequence, confirmLabel, cancelLabel, destructive } = open ? current : last;
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<{
    message: string;
    requestId: string | undefined;
    action: ReactNode;
  } | null>(null);
  // Rising edge only. Clearing when `open` falls would drop the alert mid-exit.
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) setError(null);
  }
  const firing = useRef(false);
  const busy = running || externalBusy;

  function close() {
    setError(null);
    onCancel();
  }

  async function confirm() {
    if (firing.current) return;
    firing.current = true;
    setRunning(true);
    setError(null);
    try {
      await onConfirm();
      close();
    } catch (err) {
      setError({
        message: errorMessage(err),
        requestId: err instanceof ApiError ? err.requestId : undefined,
        action: errorAction?.(err) ?? null,
      });
    } finally {
      firing.current = false;
      setRunning(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : close}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <ActionErrorAlert error={error} action={error?.action} sx={{ mb: 2 }} />
        <Typography>{consequence}</Typography>
      </DialogContent>
      <DialogActions>
        {/* Initial focus on the dismiss, so Enter cannot fire the action (DLG-004). */}
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <Button autoFocus onClick={close} disabled={busy}>
          {cancelLabel ?? t('common.cancel')}
        </Button>
        <SubmitButton
          color={destructive ? 'error' : 'primary'}
          loading={busy}
          onClick={() => void confirm()}
        >
          {confirmLabel}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
